/*
 * MarineMaker lettering font picker (product.order-boat-name, "Lettering style").
 *
 * Mount: <div data-mm-font-picker data-name="Lettering style" data-preview="Boat name (exactly as it should appear)" data-required>
 * Renders a dropdown: a toggle button and a panel with search, category filters and one native radio per
 * font (name="properties[<data-name>]"), so the choice submits as a line-item property and `required`
 * validation works. Each option previews the customer's boat name in that font.
 *
 * Fonts come from Google Fonts (all SIL Open Font License or Apache, so commercial cutting is allowed).
 * They load only when the panel first opens, subset with `text=` to just the characters of the preview,
 * so the whole catalogue costs a few kB per font. The submitted value names the exact family and weight.
 *
 * To add a font: [family, category, weight (default 400), italic (1)]. Check it exists on fonts.google.com.
 */
(() => {
  // prettier-ignore
  const FONTS = /*FONTS*/[
    ["Great Vibes","script"],["Allura","script"],["Alex Brush","script"],["Pinyon Script","script"],
    ["Parisienne","script"],["Italianno","script"],["Arizonia","script"],["Petit Formal Script","script"],
    ["Dancing Script","script",700],["Pacifico","script"],["Satisfy","script"],["Sacramento","script"],
    ["Kaushan Script","script"],["Yellowtail","script"],["Lobster","script"],["Courgette","script"],
    ["Rochester","script"],["Mr Dafoe","script"],["Cookie","script"],["Damion","script"],
    ["Norican","script"],["Playball","script"],["Leckerli One","script"],["Caveat Brush","script"],
    ["Playfair Display","serif",700],["Playfair Display","serif",700,1],["DM Serif Display","serif"],
    ["Abril Fatface","serif"],["Bodoni Moda","serif",700],["Bodoni Moda","serif",700,1],["Cinzel","serif",700],
    ["Cinzel Decorative","serif",700],["Cormorant Garamond","serif",700],["Cormorant Garamond","serif",700,1],
    ["Libre Baskerville","serif",700],["Libre Baskerville","serif",400,1],["Lora","serif",700],
    ["Merriweather","serif",900],["Prata","serif"],["Old Standard TT","serif",700],["Yeseva One","serif"],
    ["Rozha One","serif"],["Marcellus","serif"],
    ["Zilla Slab","slab",700],["Zilla Slab","slab",700,1],["Roboto Slab","slab",800],["Arvo","slab",700],
    ["Bitter","slab",800],["Josefin Slab","slab",700],["Rokkitt","slab",800],["Alfa Slab One","slab"],
    ["Ultra","slab"],["Patua One","slab"],
    ["Montserrat","sans",800],["Montserrat","sans",800,1],["Oswald","sans",600],["Bebas Neue","sans"],
    ["Archivo Black","sans"],["Anton","sans"],["Raleway","sans",800],["Poppins","sans",700],
    ["Poppins","sans",700,1],["Jost","sans",600],["Josefin Sans","sans",700],["Barlow Condensed","sans",700],
    ["Barlow Condensed","sans",700,1],["Saira Condensed","sans",800],["Teko","sans",600],["Exo 2","sans",800,1],
    ["Russo One","sans"],["Michroma","sans"],["Orbitron","sans",800],["Audiowide","sans"],
    ["Racing Sans One","display"],["Bungee","display"],["Righteous","display"],["Black Ops One","display"],
    ["Stardos Stencil","display",700],["Allerta Stencil","display"],["Passion One","display"],
    ["Titan One","display"],["Luckiest Guy","display"],["Shrikhand","display"],["Sancreek","display"],
    ["Rye","display"],["Graduate","display"],["Holtwood One SC","display"],["Diplomata","display"],
    ["Special Elite","display"],["Pirata One","display"],["UnifrakturMaguntia","display"],
    ["Fredericka the Great","display"],["Faster One","display"]
  ]/*END*/;

  const CATS = { all: 'All', script: 'Script', serif: 'Serif', slab: 'Slab', sans: 'Sans serif', display: 'Display' };
  const FALLBACK = { script: 'cursive', serif: 'serif', slab: 'serif', sans: 'sans-serif', display: 'sans-serif' };
  const WEIGHT_NAMES = { 400: '', 600: 'SemiBold', 700: 'Bold', 800: 'ExtraBold', 900: 'Black' };
  const SPECIAL = [
    ['Match my current lettering', 'We match the lettering in your photos.'],
    ['Recommend a style', 'We suggest a font on your proof.']
  ];
  const DEFAULT_PREVIEW = 'Halcyon';
  const BATCH = 20; // families per Google Fonts request (keeps URLs short)

  const fonts = FONTS.map(([family, cat, weight = 400, italic = 0]) => {
    const style = [WEIGHT_NAMES[weight], italic ? 'Italic' : ''].filter(Boolean).join(' ');
    return { family, cat, weight, italic: !!italic, label: style ? `${family} ${style}` : family };
  });

  const el = (tag, attrs = {}, text) => {
    const n = document.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => n.setAttribute(k, v));
    if (text != null) n.textContent = text;
    return n;
  };
  const cssFamily = (f) => `'${f.family}', ${FALLBACK[f.cat]}`;

  // Google Fonts CSS2: one `family=` per family, listing every weight/italic the catalogue uses.
  function fontUrls(text) {
    const byFamily = new Map();
    fonts.forEach((f) => {
      if (!byFamily.has(f.family)) byFamily.set(f.family, []);
      byFamily.get(f.family).push(f);
    });
    const params = [...byFamily].map(([family, list]) => {
      const fam = encodeURIComponent(family).replace(/%20/g, '+');
      if (list.some((f) => f.italic)) {
        const tuples = [...new Set(list.map((f) => `${f.italic ? 1 : 0},${f.weight}`))].sort();
        return `family=${fam}:ital,wght@${tuples.join(';')}`;
      }
      const weights = [...new Set(list.map((f) => f.weight))].sort((a, b) => a - b);
      return weights.length === 1 && weights[0] === 400 ? `family=${fam}` : `family=${fam}:wght@${weights.join(';')}`;
    });
    const urls = [];
    for (let i = 0; i < params.length; i += BATCH) {
      urls.push(`https://fonts.googleapis.com/css2?${params.slice(i, i + BATCH).join('&')}&text=${encodeURIComponent(text)}&display=swap`);
    }
    return urls;
  }

  function init(root) {
    const name = root.dataset.name || 'Lettering style';
    const prop = `properties[${name}]`;
    const form = document.querySelector('form.js-product-form-main');
    const source = root.dataset.preview ? document.querySelector(`[name="properties[${CSS.escape(root.dataset.preview)}]"]`) : null;
    const uid = `mm-fontpick-${Math.random().toString(36).slice(2, 8)}`;
    const loadedChars = new Set();
    let fontsWanted = false;
    let links = [];

    const previewText = () => (source && source.value.trim()) || DEFAULT_PREVIEW;

    // ---------- markup ----------
    root.classList.add('mm-fontpick');
    const label = el('span', { class: 'mm-legend', id: `${uid}-label` }, name);
    if (root.hasAttribute('data-required')) label.append(el('span', { class: 'mm-req', 'aria-hidden': 'true' }, ' *'));
    const toggle = el('button', { type: 'button', class: 'mm-fontpick__toggle', 'aria-expanded': 'false', 'aria-controls': `${uid}-panel`, 'aria-labelledby': `${uid}-label ${uid}-current` });
    const current = el('span', { class: 'mm-fontpick__current', id: `${uid}-current` });
    const currentPreview = el('span', { class: 'mm-fontpick__current-preview' }, 'Choose a font');
    const currentName = el('span', { class: 'mm-fontpick__current-name' }, `${fonts.length} fonts, each previewing your boat name`);
    current.append(currentPreview, currentName);
    toggle.append(current, el('span', { class: 'mm-fontpick__chevron', 'aria-hidden': 'true' }));

    const panel = el('div', { class: 'mm-fontpick__panel', id: `${uid}-panel`, hidden: '' });
    const search = el('input', { type: 'search', class: 'mm-fontpick__search', placeholder: 'Search fonts', 'aria-label': 'Search fonts', autocomplete: 'off', enterkeyhint: 'search' });
    const cats = el('div', { class: 'mm-fontpick__cats', role: 'group', 'aria-label': 'Font categories' });
    Object.entries(CATS).forEach(([key, text]) => {
      cats.append(el('button', { type: 'button', class: 'mm-fontpick__cat', 'data-cat': key, 'aria-pressed': String(key === 'all') }, text));
    });
    const list = el('fieldset', { class: 'mm-fontpick__list' });
    list.append(el('legend', { class: 'visually-hidden' }, name));
    const count = el('p', { class: 'mm-fontpick__count', role: 'status', 'aria-live': 'polite' });

    const option = (value, previewNode, meta, extra = {}) => {
      const opt = el('label', { class: 'mm-fontpick__opt', ...extra });
      const input = el('input', { type: 'radio', name: prop, value, 'data-mm-prop': '' });
      if (form && form.id) input.setAttribute('form', form.id);
      opt.append(input, previewNode, el('span', { class: 'mm-fontpick__meta' }, meta));
      list.append(opt);
      return input;
    };
    SPECIAL.forEach(([value, help]) => {
      option(value, el('span', { class: 'mm-fontpick__preview mm-fontpick__preview--ui' }, value), help, { 'data-cat': 'special', 'data-search': value.toLowerCase() });
    });
    const previews = [];
    fonts.forEach((f) => {
      const p = el('span', { class: 'mm-fontpick__preview', style: `font-family: ${cssFamily(f)}; font-weight: ${f.weight}; font-style: ${f.italic ? 'italic' : 'normal'};` }, DEFAULT_PREVIEW);
      previews.push(p);
      option(f.label, p, `${f.label} · ${CATS[f.cat]}`, { 'data-cat': f.cat, 'data-search': `${f.label} ${CATS[f.cat]}`.toLowerCase() });
    });
    const radios = [...list.querySelectorAll('input[type=radio]')];
    if (root.hasAttribute('data-required')) radios[0].required = true;

    panel.append(search, cats, count, list);
    root.append(label, toggle, panel);

    // ---------- fonts ----------
    function loadFonts() {
      if (!fontsWanted) return;
      const text = previewText();
      const missing = [...text].filter((c) => !loadedChars.has(c));
      if (!missing.length && links.length) return;
      missing.forEach((c) => loadedChars.add(c));
      const chars = [...loadedChars].sort().join('');
      const old = links;
      links = fontUrls(chars).map((href) => {
        const l = el('link', { rel: 'stylesheet', href });
        document.head.append(l);
        return l;
      });
      // Drop the previous subsets once the new ones are in, so the cascade stays small.
      Promise.all(links.map((l) => new Promise((r) => { l.onload = l.onerror = r; }))).then(() => old.forEach((l) => l.remove()));
    }

    let debounce;
    function updatePreviews() {
      const text = previewText();
      previews.forEach((p) => { p.textContent = text; });
      syncCurrent();
      clearTimeout(debounce);
      debounce = setTimeout(loadFonts, 400);
    }
    if (source) source.addEventListener('input', updatePreviews);

    // ---------- filtering ----------
    let cat = 'all';
    function filter() {
      const q = search.value.trim().toLowerCase();
      let shown = 0;
      list.querySelectorAll('.mm-fontpick__opt').forEach((opt) => {
        const special = opt.dataset.cat === 'special';
        const ok = (special ? !q && cat === 'all' : cat === 'all' || opt.dataset.cat === cat) && (!q || opt.dataset.search.includes(q));
        opt.hidden = !ok;
        if (ok && !special) shown++;
      });
      count.textContent = shown ? `${shown} ${shown === 1 ? 'font' : 'fonts'}` : 'No fonts match. Try another name, or choose “Recommend a style”.';
    }
    search.addEventListener('input', filter);
    cats.addEventListener('click', (e) => {
      const b = e.target.closest('.mm-fontpick__cat');
      if (!b) return;
      cat = b.dataset.cat;
      cats.querySelectorAll('.mm-fontpick__cat').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      filter();
    });

    // ---------- open / close ----------
    const isOpen = () => !panel.hidden;
    function open(focusInside = true) {
      if (isOpen()) return;
      panel.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      fontsWanted = true;
      loadFonts();
      const checked = radios.find((r) => r.checked);
      if (checked) checked.closest('.mm-fontpick__opt').scrollIntoView({ block: 'nearest' });
      if (focusInside && window.matchMedia('(pointer: fine)').matches) search.focus();
    }
    function close(returnFocus = true) {
      if (!isOpen()) return;
      panel.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      if (returnFocus) toggle.focus();
    }
    toggle.addEventListener('click', () => (isOpen() ? close() : open()));
    // Close on an outside *click* (not pointerdown): collapsing the panel earlier would shift the page
    // under the cursor and the click would miss its target (for example Add to cart below the picker).
    document.addEventListener('click', (e) => { if (isOpen() && !root.contains(e.target)) close(false); });
    root.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isOpen()) { e.preventDefault(); close(); }
      // Enter would submit the product form (implicit submission); here it confirms the choice instead.
      if (e.key === 'Enter' && panel.contains(e.target) && e.target.tagName === 'INPUT') {
        e.preventDefault();
        if (e.target.type === 'radio') close();
        else (radios.find((r) => r.checked && !r.closest('[hidden]')) || radios.find((r) => !r.closest('[hidden]')))?.focus();
      }
    });

    // ---------- selection ----------
    function syncCurrent() {
      const checked = radios.find((r) => r.checked);
      if (!checked) return;
      const f = fonts.find((x) => x.label === checked.value);
      root.classList.add('has-value');
      if (f) {
        currentPreview.textContent = previewText();
        currentPreview.style.cssText = `font-family: ${cssFamily(f)}; font-weight: ${f.weight}; font-style: ${f.italic ? 'italic' : 'normal'};`;
        currentName.textContent = f.label;
      } else {
        currentPreview.textContent = checked.value;
        currentPreview.style.cssText = '';
        currentName.textContent = SPECIAL.find(([v]) => v === checked.value)[1];
      }
    }
    list.addEventListener('change', () => { root.classList.remove('mm-invalid'); syncCurrent(); });
    // A mouse/touch pick closes the dropdown; arrow keys move through the list without closing it.
    let pointerPick = false;
    list.addEventListener('pointerdown', () => { pointerPick = true; });
    list.addEventListener('keydown', () => { pointerPick = false; });
    list.addEventListener('change', () => { if (pointerPick) setTimeout(() => close(), 0); pointerPick = false; });

    // Native validation can't focus a radio in a closed panel, so open it first.
    radios[0].addEventListener('invalid', () => { root.classList.add('mm-invalid'); open(false); });

    updatePreviews();
    filter();
    // Restore after back/forward navigation (the browser may keep the radio checked).
    if (radios.some((r) => r.checked)) { fontsWanted = true; loadFonts(); syncCurrent(); }
  }

  function start() { document.querySelectorAll('[data-mm-font-picker]:not(.mm-fontpick)').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
